//src/pages/PaymentDeliveryPage.js
import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import TopBanner from "../components/TopBanner";
import MainHeader from "../components/MainHeader";
import Footer from "../components/Footer";
import CartDrawer from "../components/CartDrawer";
import AddedToBagToast from "../components/AddedToBagToast";
import { LanguageContext } from "../context/LanguageContext";
import { getPaymentDeliverySettings } from "../services/homepage.api";
import { apiRequest } from "../services/api";
import { getActiveFreeShippingDiscount } from "../services/discounts.api";
import { buildFreeShippingInfo } from "../utils/freeShippingDiscount";

function PaymentDeliveryPage() {
  const { lang } = useContext(LanguageContext);
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
const [loading, setLoading] = useState(true);

const [shipping, setShipping] = useState({
  fee: 4,
  free_enabled: false,
  free_threshold: 0,
});

const [freeShippingDiscount, setFreeShippingDiscount] = useState(null);

  useEffect(() => {
  let mounted = true;

  setLoading(true);

  getPaymentDeliverySettings()
    .then((data) => {
      if (mounted) setSettings(data);
    })
    .catch(() => {
      if (mounted) setSettings(null);
    })
    .finally(() => {
      if (mounted) setLoading(false);
    });

  return () => {
    mounted = false;
  };
}, []);

useEffect(() => {
  apiRequest("/shipping-settings")
    .then((s) =>
      setShipping({
        fee: Number(s.default_fee ?? 4),
        free_enabled: !!s.free_enabled,
        free_threshold: Number(s.free_threshold ?? 0),
      })
    )
    .catch(() => {});

  getActiveFreeShippingDiscount()
    .then(setFreeShippingDiscount)
    .catch(() => setFreeShippingDiscount(null));
}, []);

  const title =
    lang === "AR"
      ? settings?.title_ar || "الدفع والتوصيل"
      : settings?.title_en || "Payment and Delivery";

  const content =
    lang === "AR"
      ? settings?.content_ar || ""
      : settings?.content_en || "";

      const safeContent =
  content.trim() ||
  (lang === "AR"
    ? "لم يتم إضافة محتوى صفحة الدفع والتوصيل بعد."
    : "Payment and delivery information has not been added yet.");

    const freeShippingInfo = buildFreeShippingInfo({
  discount: freeShippingDiscount,
  subtotal: 0,
  totalItems: 0,
  lang,
});

const deliveryFeeLabel = (() => {
  const fee = Number(shipping.fee || 0);
  if (fee <= 0) return lang === "AR" ? "مجاني" : "Free";
  return `$${fee.toFixed(2)}`;
})();

const hasFreeShippingNoCondition =
  freeShippingInfo.hasDiscount &&
  freeShippingInfo.minRequirement === "none";

const showDeliveryFeeIcon = !hasFreeShippingNoCondition;
const showFreeDeliveryIcon = freeShippingInfo.hasDiscount;

const DeliveryIcon = () => (
  <svg viewBox="0 0 48 48" className="w-[56px] h-[56px]" fill="none">
    <path d="M4 16H27V34H4V16Z" fill="#cfcfcf" />
    <path d="M27 22H37L43 29V34H27V22Z" fill="#cfcfcf" />
    <circle cx="14" cy="36" r="4" fill="#cfcfcf" />
    <circle cx="35" cy="36" r="4" fill="#cfcfcf" />
    <path d="M10 23L15 28L24 17" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const FreeDeliveryIcon = () => (
  <svg viewBox="0 0 48 48" className="w-[56px] h-[56px]" fill="none">
    <path d="M6 16H28V34H6V16Z" fill="#cfcfcf" />
    <path d="M28 22H38L43 28V34H28V22Z" fill="#cfcfcf" />
    <circle cx="15" cy="36" r="4" fill="#cfcfcf" />
    <circle cx="36" cy="36" r="4" fill="#cfcfcf" />
    <path d="M13 24L18 29L28 18" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M34 10V18" stroke="#cfcfcf" strokeWidth="4" strokeLinecap="round" />
    <path d="M30 14H38" stroke="#cfcfcf" strokeWidth="4" strokeLinecap="round" />
  </svg>
);

const PickupIcon = () => (
  <svg viewBox="0 0 48 48" className="w-[56px] h-[56px]" fill="none">
    <path d="M9 18H39L36 40H12L9 18Z" fill="#cfcfcf" />
    <path
      d="M15 18C15 12.5 18.8 8 24 8C29.2 8 33 12.5 33 18"
      stroke="#cfcfcf"
      strokeWidth="4"
      strokeLinecap="round"
    />
    <path
      d="M17 28L22 33L32 23"
      stroke="white"
      strokeWidth="4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const DeliveryBenefits = () => (
  <section className="mb-8">
    <div className="flex flex-wrap items-start justify-center gap-8 text-center">
      {showDeliveryFeeIcon && (
        <div className="w-[145px] flex flex-col items-center">
          <DeliveryIcon />

          <p className="mt-2 text-[14px] font-extrabold uppercase leading-[1.25]">
            {lang === "AR" ? "رسوم التوصيل" : "Delivery fees"}
          </p>

          <p className="mt-2 text-[14px] font-extrabold">
            {deliveryFeeLabel}
          </p>
        </div>
      )}

      {showFreeDeliveryIcon && (
        <div className="w-[145px] flex flex-col items-center">
          <FreeDeliveryIcon />

          <p className="mt-2 text-[14px] font-extrabold uppercase leading-[1.25]">
            {lang === "AR" ? "توصيل مجاني" : "Free delivery"}
          </p>

          <p className="mt-2 text-[14px] font-extrabold">
            {freeShippingInfo.conditionLabel}
          </p>

          {freeShippingInfo.endLabel && (
            <p className="mt-1 text-[12px] text-gray-600 leading-[1.25]">
              {lang === "AR"
                ? `ينتهي في ${freeShippingInfo.endLabel}`
                : `Ends on ${freeShippingInfo.endLabel}`}
            </p>
          )}
        </div>
      )}

      <div className="w-[145px] flex flex-col items-center">
        <PickupIcon />

        <p className="mt-2 text-[14px] font-extrabold uppercase leading-[1.25]">
          {lang === "AR" ? "استلام من المحل" : "Pick up from store"}
        </p>

        <p className="mt-2 text-[14px] font-extrabold">
          {lang === "AR" ? "مجاني" : "Free"}
        </p>
      </div>
    </div>
  </section>
);

    if (loading) {
  return (
    <div className="w-full min-h-screen bg-white flex flex-col">
      <TopBanner />
      <MainHeader />

      <CartDrawer />
      <AddedToBagToast />

      <main
        dir={lang === "AR" ? "rtl" : "ltr"}
        className="w-full px-5 md:px-10 pt-8 md:pt-[170px] pb-12"
      >
        <div className="max-w-5xl mx-auto">
          <div className="h-4 w-[260px] bg-gray-200 animate-pulse rounded mb-10" />
          <div className="h-9 w-[330px] bg-gray-200 animate-pulse rounded mb-3" />
          <div className="h-px bg-gray-200 mb-8" />

          <div className="space-y-4">
            <div className="h-5 w-full bg-gray-200 animate-pulse rounded" />
            <div className="h-5 w-[94%] bg-gray-200 animate-pulse rounded" />
            <div className="h-5 w-[88%] bg-gray-200 animate-pulse rounded" />
            <div className="h-5 w-[76%] bg-gray-200 animate-pulse rounded" />
            <div className="h-5 w-[92%] bg-gray-200 animate-pulse rounded" />
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

  return (
    <div className="w-full min-h-screen bg-white flex flex-col">
      <TopBanner />
      <MainHeader />

      <CartDrawer />
      <AddedToBagToast />

      <main
        dir={lang === "AR" ? "rtl" : "ltr"}
        className="w-full px-5 md:px-10 pt-8 md:pt-[170px] pb-12"
      >
        <div className="max-w-5xl mx-auto">
          <div className="text-[14px] text-gray-500 mb-10">
  <button
    type="button"
    onClick={() => {
      navigate("/");
      setTimeout(() => window.scrollTo({ top: 0, left: 0 }), 0);
    }}
    className="hover:text-black hover:underline transition"
  >
    {lang === "AR" ? "الرئيسية" : "Main page"}
  </button>

  <span className="mx-2">›</span>
  ETIKA
  <span className="mx-2">›</span>
  {title}
</div>

          <h1 className="text-[24px] md:text-[34px] font-semibold text-black mb-3">
            {title}
          </h1>

          <div className="h-px bg-gray-300 mb-8" />

          <DeliveryBenefits />

<div className="whitespace-pre-line text-[16px] md:text-[18px] leading-8 text-gray-800">
  {safeContent}
</div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

export default PaymentDeliveryPage;